import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useDebugStore } from "../../store/debugStore";
import { useUserStore } from "../../store/userStore";
import { UniversalDirectoryManager } from "../storage/universalDirectoryManager";
import type { VCardCategory } from "../../types/vcard";

interface SyncOptions {
  source?: "profile" | "vcard";
  priority?: "profile" | "vcard"; // Which data takes precedence in conflicts
  batchUpdates?: boolean;
  batch?: boolean;
  debounceMs?: number;
}

interface SyncFieldParams {
  fieldId: string;
  category: string;
  value: any;
  sourceType?: "profile" | "vcard";
  vcardId: string;
  path?: string[];
  options?: Partial<SyncOptions>;
}

// Singleton instance to maintain state
export class VCardSynchronization {
  private static instance: VCardSynchronization;
  private static listeners: Map<string, Set<(value: any) => void>> = new Map();
  private static listenerKeys: Map<(value: any) => void, Set<string>> =
    new Map();
  private static updateCounts: Map<string, number> = new Map();
  private static lastSyncTimes: Map<string, number> = new Map();
  private static pendingUpdates: Array<{ key: string; value: any }> = [];
  private static timeouts: Map<string, NodeJS.Timeout> = new Map();
  private static pendingWrites: Map<
    string,
    { userId: string; docId: string; data: Record<string, any> }
  > = new Map();
  private static writeTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private static lastValues: Map<string, any> = new Map();
  private static readonly SYNC_TIMEOUT = 30000; // 30 seconds
  private static readonly CLEANUP_INTERVAL = 60000; // 1 minute
  private static readonly LINKED_CATEGORIES: VCardCategory[] = [];
  private static readonly PERFORMANCE_THRESHOLD = 50; // ms
  private static performanceMetrics: {
    updates: { [key: string]: number[] };
    listeners: { [key: string]: number };
  } = {
    updates: {},
    listeners: {},
  };

  private constructor() {
    // Start cleanup interval
    setInterval(() => {
      this.cleanup();
    }, VCardSynchronization.CLEANUP_INTERVAL);
  }

  public static getInstance(): VCardSynchronization {
    if (!VCardSynchronization.instance) {
      VCardSynchronization.instance = new VCardSynchronization();
    }
    return VCardSynchronization.instance;
  }

  private static getLinkedCategories(category: VCardCategory): VCardCategory[] {
    if (this.LINKED_CATEGORIES.length === 0) {
      return [category];
    }

    if (this.LINKED_CATEGORIES.includes(category)) {
      return Array.from(new Set(this.LINKED_CATEGORIES));
    }

    return [category];
  }

  private cleanup() {
    const now = Date.now();

    // Clear old sync times
    for (const [key, time] of VCardSynchronization.lastSyncTimes.entries()) {
      if (now - time > VCardSynchronization.SYNC_TIMEOUT) {
        VCardSynchronization.lastSyncTimes.delete(key);
      }
    }

    // Clear stale timeouts
    for (const [key, timeout] of VCardSynchronization.timeouts.entries()) {
      clearTimeout(timeout);
      VCardSynchronization.timeouts.delete(key);
    }

    // Process any stuck pending updates
    for (const update of VCardSynchronization.pendingUpdates) {
      this.processPendingUpdate(update.key, update.value);
    }
  }

  private async processPendingUpdate(key: string, update: any) {
    const listeners = VCardSynchronization.listeners.get(key);
    if (!listeners) {
      VCardSynchronization.pendingUpdates =
        VCardSynchronization.pendingUpdates.filter(
          (update) => update.key !== key,
        );
      return;
    }

    try {
      // Notify all listeners
      for (const listener of listeners) {
        await Promise.resolve(listener(update));
      }

      // Clear pending update
      VCardSynchronization.pendingUpdates =
        VCardSynchronization.pendingUpdates.filter(
          (update) => update.key !== key,
        );
      VCardSynchronization.lastSyncTimes.set(key, Date.now());

      // Clear any associated timeout
      const timeout = VCardSynchronization.timeouts.get(key);
      if (timeout) {
        clearTimeout(timeout);
        VCardSynchronization.timeouts.delete(key);
      }
    } catch (error) {
      console.error(`Failed to process update for ${key}:`, error);
    }
  }

  public static registerFieldListener(
    fieldId: string,
    category: VCardCategory,
    callback: (value: any) => void,
  ): () => void {
    const key = `${category}_${fieldId}`;

    // Check if callback is already registered for this key
    if (!this.listenerKeys.has(callback)) {
      this.listenerKeys.set(callback, new Set());
    }
    const callbackKeys = this.listenerKeys.get(callback)!;

    if (callbackKeys.has(key)) {
      console.warn(`Duplicate listener registration for ${key}`);
      return () => this.unregisterFieldListener(key, callback);
    }

    // Initialize listener set if it doesn't exist
    if (!this.listeners.has(key)) {
      this.listeners.set(key, new Set());
      this.updateCounts.set(key, 0);
    }

    // Add the listener
    this.listeners.get(key)!.add(callback);
    callbackKeys.add(key);

    // Return cleanup function
    return () => this.unregisterFieldListener(key, callback);
  }

  private static unregisterFieldListener(
    key: string,
    callback: (value: any) => void,
  ): void {
    const listeners = this.listeners.get(key);
    if (listeners) {
      listeners.delete(callback);

      // Remove callback key mapping
      const callbackKeys = this.listenerKeys.get(callback);
      if (callbackKeys) {
        callbackKeys.delete(key);
        if (callbackKeys.size === 0) {
          this.listenerKeys.delete(callback);
        }
      }

      // Clean up if no listeners remain
      if (listeners.size === 0) {
        this.listeners.delete(key);
        this.updateCounts.delete(key);
        this.lastSyncTimes.delete(key);
        const timeout = this.timeouts.get(key);
        if (timeout) {
          clearTimeout(timeout);
          this.timeouts.delete(key);
        }
      }
    }
  }

  private static recordUpdateTime(key: string, duration: number) {
    if (!this.performanceMetrics.updates[key]) {
      this.performanceMetrics.updates[key] = [];
    }
    this.performanceMetrics.updates[key].push(duration);

    // Log slow updates
    if (duration > this.PERFORMANCE_THRESHOLD) {
      console.warn(`Slow update for ${key}: ${duration}ms`);
    }
  }

  private static isEqual(value1: any, value2: any): boolean {
    if (value1 === value2) return true;
    if (value1 == null && value2 == null) return true;
    if (value1 == null || value2 == null) return false;
    try {
      return JSON.stringify(value1) === JSON.stringify(value2);
    } catch {
      return false;
    }
  }

  private static stripUndefined(value: any): any {
    if (Array.isArray(value)) {
      return value
        .map((item) => this.stripUndefined(item))
        .filter((item) => item !== undefined);
    }

    if (value && typeof value === "object") {
      const result: Record<string, unknown> = {};
      Object.entries(value).forEach(([key, val]) => {
        if (val === undefined) return;
        const cleaned = this.stripUndefined(val);
        if (cleaned !== undefined) {
          result[key] = cleaned;
        }
      });
      return result;
    }

    return value;
  }

  private static queueWrite(
    key: string,
    userId: string,
    docId: string,
    data: Record<string, any>,
    debounceMs: number,
  ) {
    const existing = this.pendingWrites.get(key);
    this.pendingWrites.set(key, {
      userId,
      docId,
      data: {
        ...(existing?.data || {}),
        ...data,
      },
    });

    const existingTimeout = this.writeTimeouts.get(key);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(async () => {
      const payload = this.pendingWrites.get(key);
      if (!payload) return;

      this.pendingWrites.delete(key);
      this.writeTimeouts.delete(key);

      const basePath = UniversalDirectoryManager.getPath({
        userId: payload.userId,
        contentType: "vcard",
        state: "active",
      });
      const docRef = doc(db, basePath, payload.docId);

      try {
        await updateDoc(docRef, payload.data);
      } catch (error) {
        const addDebugMessage = useDebugStore.getState().addMessage;
        addDebugMessage(
          "error",
          `Failed to sync ${payload.docId} (queued)`,
          error,
        );
        throw error;
      }
    }, debounceMs);

    this.writeTimeouts.set(key, timeout);
  }

  public static async syncField(
    paramsOrUserId: SyncFieldParams | string,
    legacyCategory?: VCardCategory,
    legacyFieldId?: string,
    legacyValue?: any,
    legacyOptions?: Partial<SyncOptions>,
  ): Promise<void> {
    let params: SyncFieldParams;
    let legacyUserId: string | undefined;

    if (typeof paramsOrUserId === "string") {
      legacyUserId = paramsOrUserId;
      params = {
        fieldId: legacyFieldId as string,
        category: legacyCategory as string,
        value: legacyValue,
        sourceType: legacyOptions?.source,
        vcardId: legacyCategory as string,
        options: legacyOptions,
      };
    } else {
      params = paramsOrUserId;
    }

    const { fieldId, category, value, path } = params;
    const options = params.options || legacyOptions;
    const sourceType = params.sourceType || options?.source || "profile";
    const startTime = performance.now();
    const addDebugMessage = useDebugStore.getState().addMessage;

    if (!fieldId || !category) {
      addDebugMessage(
        "error",
        "syncField called without required fieldId/category",
      );
      return;
    }

    const fieldPath = path?.length ? path.join(".") : fieldId;
    const sanitizedValue = this.stripUndefined(value);

    if (sanitizedValue === undefined) {
      addDebugMessage(
        "warning",
        `syncField skipped undefined value for ${fieldPath}`,
      );
      return;
    }

    const valueKey = `${category}_${fieldPath}`;
    const lastValue = this.lastValues.get(valueKey);

    if (this.isEqual(lastValue, sanitizedValue)) {
      addDebugMessage(
        "info",
        `Skipping sync for ${fieldPath} - value unchanged`,
      );
      return;
    }

    this.lastValues.set(valueKey, sanitizedValue);

    // Get all linked categories that should receive this update
    const categories = this.getLinkedCategories(category);

    // Update each linked category
    await Promise.all(
      categories.map(async (linkedCategory) => {
        const key = `${linkedCategory}_${fieldId}`;
        const updateStart = performance.now();

        // Update sync time
        this.lastSyncTimes.set(key, Date.now());

        // Get listeners for this category
        const listeners = this.listeners.get(key);
        if (!listeners) return;

        // Update count
        const currentCount = this.updateCounts.get(key) || 0;
        this.updateCounts.set(key, currentCount + 1);

        // Notify listeners
        await Promise.all(
          Array.from(listeners).map(async (callback) => {
            try {
              await Promise.resolve(callback(value));
            } catch (error) {
              console.error(`Error in listener for ${key}:`, error);
            }
          }),
        );

        // Record performance metrics
        this.recordUpdateTime(key, performance.now() - updateStart);
      }),
    );

    // Persist to Firestore for the originating category
    const profile = useUserStore.getState().profile;
    const userId = legacyUserId || profile?.id;
    const docId = params.vcardId || category;

    if (!userId) {
      addDebugMessage(
        "error",
        `syncField skipped Firestore write (missing userId) for ${category}`,
      );
    } else {
      const basePath = UniversalDirectoryManager.getPath({
        userId,
        contentType: "vcard",
        state: "active",
      });
      const docRef = doc(db, basePath, docId);
      const updatePayload: Record<string, any> = {
        [fieldPath]: sanitizedValue,
        updatedAt: serverTimestamp(),
        lastUpdateSource: sourceType,
      };

      const debounceMs =
        options?.debounceMs ??
        (options?.batchUpdates || options?.batch ? 100 : 0);
      if (debounceMs > 0 || options?.batchUpdates || options?.batch) {
        this.queueWrite(
          `${userId}:${docId}`,
          userId,
          docId,
          updatePayload,
          debounceMs,
        );
      } else {
        try {
          await updateDoc(docRef, updatePayload);
        } catch (error) {
          addDebugMessage(
            "error",
            `Failed to sync ${docId} (${fieldPath})`,
            error,
          );
          throw error;
        }
      }
    }

    // Record overall sync performance
    const totalDuration = performance.now() - startTime;
    if (totalDuration > this.PERFORMANCE_THRESHOLD) {
      console.warn(`Slow overall sync for ${fieldId}: ${totalDuration}ms`);
    }
  }

  public static getDebugInfo() {
    const now = Date.now();
    return {
      listeners: Array.from(this.listeners.entries()).map(
        ([key, listeners]) => ({
          key,
          count: listeners.size,
          lastSync: this.lastSyncTimes.get(key),
          timeSinceSync: now - (this.lastSyncTimes.get(key) || 0),
          duplicateCount: this.getDuplicateCount(key),
          performance: {
            avgUpdateTime: this.getAverageUpdateTime(key),
            lastUpdateTimes:
              this.performanceMetrics.updates[key]?.slice(-5) || [],
          },
        }),
      ),
      timeouts: Array.from(this.timeouts.keys()),
      lastSyncTimes: Array.from(this.lastSyncTimes.entries()),
      pendingUpdates: this.pendingUpdates || [],
      performance: {
        totalUpdates: Object.values(this.performanceMetrics.updates).reduce(
          (sum, times) => sum + times.length,
          0,
        ),
        slowUpdates: Object.entries(this.performanceMetrics.updates).reduce(
          (count, [, times]) =>
            count + times.filter((t) => t > this.PERFORMANCE_THRESHOLD).length,
          0,
        ),
      },
    };
  }

  private static getAverageUpdateTime(key: string): number {
    const times = this.performanceMetrics.updates[key];
    if (!times || times.length === 0) return 0;
    return times.reduce((sum, time) => sum + time, 0) / times.length;
  }

  public static getActiveListeners(): Array<{
    category: string;
    fields: string[];
    updateCount: number;
    status: string;
  }> {
    const listenersByCategory = new Map<string, Set<string>>();

    for (const [key] of this.listeners) {
      const [category, field] = key.split("_");
      if (!listenersByCategory.has(category)) {
        listenersByCategory.set(category, new Set());
      }
      listenersByCategory.get(category)!.add(field);
    }

    // Convert to array format
    return Array.from(listenersByCategory.entries()).map(
      ([category, fields]) => {
        const updateCount = Array.from(fields).reduce((count, field) => {
          const key = `${category}_${field}`;
          const hasPending = this.pendingUpdates.some(
            (update) => update.key === key,
          );
          const hasTimeout = this.timeouts.has(key);
          return count + (hasPending || hasTimeout ? 1 : 0);
        }, 0);

        return {
          category,
          fields: Array.from(fields),
          updateCount,
          status: "active",
        };
      },
    );
  }

  public static getFieldListenerCount(
    fieldId: string,
    category: string,
  ): number {
    const key = `${category}_${fieldId}`;
    return this.listeners.get(key)?.size || 0;
  }

  private static getDuplicateCount(key: string): number {
    const listeners = this.listeners.get(key);
    if (!listeners) return 0;

    const uniqueCallbacks = new Set(
      Array.from(listeners).map((callback) =>
        Array.from(this.listenerKeys.get(callback) || []).join(","),
      ),
    );

    return listeners.size - uniqueCallbacks.size;
  }

  public static cleanup(): void {
    this.listeners.clear();
    this.listenerKeys.clear();
    this.updateCounts.clear();
    this.lastSyncTimes.clear();
    this.timeouts.forEach((timeout) => clearTimeout(timeout));
    this.timeouts.clear();
    this.performanceMetrics = { updates: {}, listeners: {} };
    this.pendingUpdates = [];
  }
}
